import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/frontend/components/ui/dialog";
import { Button } from "@/frontend/components/ui/button";
import { Alert, AlertDescription } from "@/frontend/components/ui/alert";
import { AlertCircle, ClipboardList, Loader2 } from "lucide-react";
import { GuestFields } from "@/frontend/components/booking/GuestFields";
import { validateGuest, type GuestFieldErrors } from "@/shared/constants/guestForm";
import type { GuestInfo } from "@/shared/types";

interface CompleteDetailsDialogProps {
  /** The student's current guest entry, used to pre-fill what we already have. */
  guest: GuestInfo;
  onSubmit: (guest: GuestInfo) => Promise<void>;
  isSaving: boolean;
}

/**
 * Blocking prompt asking the student to supply the personal details their
 * booking is missing.
 *
 * Bookings taken before the DEV2-1038 write-path fix only ever stored name,
 * email and phone: the rest of what the student filled in on the booking form
 * was dropped on write and cannot be recovered, so we ask for it once here.
 *
 * Reuses the booking form's fieldset and validator, so the student sees the same
 * fields and the same rules they met when booking. Name and email are locked:
 * they are the login identity and appear on the signed contract.
 */
export const CompleteDetailsDialog = ({
  guest,
  onSubmit,
  isSaving,
}: CompleteDetailsDialogProps) => {
  const [draft, setDraft] = useState<GuestInfo>(guest);
  const [errors, setErrors] = useState<GuestFieldErrors>({});
  const [showErrors, setShowErrors] = useState(false);

  const handleUpdate = (field: keyof GuestInfo, value: string) => {
    setDraft((prev) => {
      const next = { ...prev, [field]: value };
      if (showErrors) setErrors(validateGuest(next));
      return next;
    });
  };

  const handleSubmit = async () => {
    const found = validateGuest(draft);
    setErrors(found);
    setShowErrors(true);
    if (Object.keys(found).length > 0) return;

    try {
      await onSubmit(draft);
    } catch {
      // Surfaced as a toast by the parent (StudentPortalGuard).
    }
  };

  return (
    <Dialog open={true} onOpenChange={() => {}}>
      {/* onOpenChange empty to prevent dismissal */}
      <DialogContent
        className="sm:max-w-lg max-h-[90vh] overflow-y-auto"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5" />
            Complete Your Details
          </DialogTitle>
          <DialogDescription>
            We are missing some of the personal information needed for your stay.
            Please confirm the details below to continue to your portal.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <Alert variant="default" className="border-blue-200 bg-blue-50">
            <AlertCircle className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800">
              This is a one-off. Your name and email are fixed because they are on
              your rental contract; contact the residence team if either is wrong.
            </AlertDescription>
          </Alert>

          <GuestFields
            guest={draft}
            index={0}
            errors={showErrors ? errors : undefined}
            onUpdate={handleUpdate}
            showEmailNotice={false}
            lockIdentity
          />
        </div>

        <DialogFooter>
          <Button onClick={handleSubmit} disabled={isSaving}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save and continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
